import { Link } from "react-router-dom";
import { MapPin, Users, Compass, Clock, Award, MessageCircle, ArrowRight } from "lucide-react";
import { useSite } from "../lib/SiteContext";
import { optImg } from "../lib/image";
import Seo from "../components/Seo";
import FadeIn from "../components/FadeIn";

const ICONS = { MapPin, Users, Compass, Clock, Award, MessageCircle };

const About = () => {
  const { content } = useSite();

  return (
    <div data-testid="about-page">
      <Seo
        title="About Us — Aloha Corfu Tours | Private Corfu Tours"
        description="Aloha Corfu Tours creates fully private tours across Corfu with local Corfiot knowledge, insider spots and years of experience. Learn about our way of exploring."
      />
      <section className="bg-pine-dark py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-seafoam">Our story</p>
            <h1 className="mt-2 font-display text-4xl font-semibold text-white sm:text-5xl">About Aloha Corfu Tours</h1>
          </FadeIn>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <FadeIn>
            <img src={optImg("https://images.pexels.com/photos/33350500/pexels-photo-33350500.jpeg", 1000)}
                 alt="Paleokastritsa bays in Corfu, one of the destinations of Aloha Corfu Tours"
                 className="aspect-[4/3] w-full rounded-2xl object-cover shadow-lift" loading="lazy" decoding="async" />
          </FadeIn>
          <FadeIn delay={0.1}>
            <h2 className="font-display text-3xl font-semibold text-pine">{content?.about_title || "Why explore Corfu with us"}</h2>
            <p className="mt-5 text-base leading-relaxed text-gray-600">{content?.about_text}</p>
            <p className="mt-4 text-base leading-relaxed text-gray-600">
              Corfu is our home, and showing it to visitors is what we love most. We created Aloha Corfu Tours
              to share the island the way we know it: not just the famous postcard views, but the quiet coves,
              village kafenia, family tavernas and viewpoints that make Corfu unforgettable.
            </p>
            <p className="mt-4 text-base leading-relaxed text-gray-600">
              Every tour is fully private and shaped around you — your dates, your pace, your interests.
              Send us an enquiry and we personally plan the details with you.
            </p>
            <Link to="/tours" className="mt-7 inline-flex items-center gap-2 rounded-full bg-pine px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-pine-light" data-testid="about-explore-button">
              Explore our tours <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="bg-seafoam-light/60 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeIn className="mb-10 max-w-2xl">
            <h2 className="font-display text-3xl font-semibold text-pine">What makes us different</h2>
          </FadeIn>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {(content?.why_us || []).map((item, i) => {
              const Icon = ICONS[item.icon] || Compass;
              return (
                <FadeIn key={item.title} delay={i * 0.06}>
                  <div className="h-full rounded-2xl border border-gray-100 bg-white p-6 shadow-soft">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-seafoam-light">
                      <Icon className="h-5 w-5 text-pine" />
                    </div>
                    <h3 className="mt-4 font-display text-lg font-semibold text-pine">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-500">{item.text}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
